import { HttpClient } from './httpClient';
import { ErrorHandler } from './errorHandler';
import { AuthManager } from './authManager';
import { AuthModule } from './authModule';
import { JobsModule } from './jobsModule';
import { ProfileModule } from './profileModule';
import { ResumeModule } from './resumeModule';
import { MessagingModule } from './messagingModule';
import { AvatarModule } from './avatarModule';
import { AdminModule } from './adminModule';
import { AnalyticsModule } from './analyticsModule';
import { SystemModule } from './systemModule';
import { ApplicationsModule } from './applicationsModule';
import { CompaniesModule } from './companiesModule';
import { JobTemplatesModule } from './jobTemplatesModule';
import { AIModule } from './aiModule';
import { CandidatesModule } from './candidatesModule';
import { InterviewsModule } from './interviewsModule';
import { TestimonialsModule } from './testimonialsModule';
import { SavedJobsModule } from './savedJobsModule';
import { ContentVersionsModule } from './contentVersionsModule';
import type { AxiosInstance } from 'axios';

/**
 * Unified API Service - Central service for all API communications
 * 
 * This service provides a unified interface for all API operations,
 * with consistent error handling, authentication, and request/response transformation.
 */
export class UnifiedApiService {
  private httpClient: HttpClient;
  private axiosInstance: AxiosInstance;
  private errorHandler: ErrorHandler;
  private authManager: AuthManager;

  // Module instances
  public auth!: AuthModule;
  public jobs!: JobsModule;
  public profile!: ProfileModule;
  public resume!: ResumeModule;
  public messaging!: MessagingModule;
  public avatar!: AvatarModule;
  public admin!: AdminModule;
  public analytics!: AnalyticsModule;
  public system!: SystemModule;
  public applications!: ApplicationsModule;
  public companies!: CompaniesModule;
  public jobTemplates!: JobTemplatesModule;
  public ai!: AIModule;
  public candidates!: CandidatesModule;
  public interviews!: InterviewsModule;
  public testimonials!: TestimonialsModule;
  public savedJobs!: SavedJobsModule;
  public contentVersions!: ContentVersionsModule;

  constructor() {
    // Initialize core infrastructure
    this.httpClient = new HttpClient();
    this.axiosInstance = this.httpClient.getInstance();
    this.errorHandler = this.httpClient.getErrorHandler();
    this.authManager = this.httpClient.getAuthManager();

    // Initialize modules (will be implemented in subsequent tasks)
    this.initializeModules();
  }

  /**
   * Initialize all service modules
   */
  private initializeModules(): void {
    // Initialize authentication module
    this.auth = new AuthModule(this.axiosInstance, this.errorHandler);
    
    // Initialize jobs module
    this.jobs = new JobsModule(this.axiosInstance, this.errorHandler);
    
    // Initialize profile module
    this.profile = new ProfileModule(this.axiosInstance, this.errorHandler);
    
    // Initialize resume module
    this.resume = new ResumeModule(this.axiosInstance, this.errorHandler);
    
    // Initialize messaging module
    this.messaging = new MessagingModule(this.axiosInstance, this.errorHandler);
    
    // Initialize avatar module
    this.avatar = new AvatarModule(this.axiosInstance, this.errorHandler);
    
    // Initialize admin module
    this.admin = new AdminModule(this.axiosInstance, this.errorHandler);
    
    // Initialize analytics module
    this.analytics = new AnalyticsModule(this.axiosInstance, this.errorHandler);
    
    // Initialize system module
    this.system = new SystemModule(this.axiosInstance, this.errorHandler);
    
    // Initialize applications module
    this.applications = new ApplicationsModule(this.axiosInstance, this.errorHandler);
    
    // Initialize companies module
    this.companies = new CompaniesModule(this.axiosInstance, this.errorHandler);
    
    // Initialize job templates module
    this.jobTemplates = new JobTemplatesModule(this.axiosInstance, this.errorHandler);
    
    // Initialize AI module
    this.ai = new AIModule(this.axiosInstance, this.errorHandler);
    
    // Initialize candidates module
    this.candidates = new CandidatesModule(this.axiosInstance, this.errorHandler);
    
    // Initialize interviews module
    this.interviews = new InterviewsModule(this.axiosInstance, this.errorHandler);
    
    // Initialize testimonials module
    this.testimonials = new TestimonialsModule(this.axiosInstance, this.errorHandler);
    
    // Initialize saved jobs module
    this.savedJobs = new SavedJobsModule(this.axiosInstance, this.errorHandler);
    
    // Initialize content versions module (Inline AI Editor)
    this.contentVersions = new ContentVersionsModule(this.axiosInstance, this.errorHandler);
  }

  /**
   * Get the HTTP client instance for direct access if needed
   */
  getHttpClient(): HttpClient {
    return this.httpClient;
  }

  /**
   * Get the Axios instance for direct access if needed
   */
  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }

  /**
   * Get the error handler instance
   */
  getErrorHandler(): ErrorHandler {
    return this.errorHandler;
  }

  /**
   * Get the auth manager instance
   */
  getAuthManager(): AuthManager {
    return this.authManager;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.authManager.isAuthenticated();
  }

  /**
   * Get current authentication token
   */
  getToken(): string | null {
    return this.authManager.getToken();
  }

  /**
   * Clear authentication and logout
   */
  async logout(): Promise<void> {
    return this.authManager.logout();
  }

  /**
   * Update HTTP client configuration
   */
  updateConfig(config: any): void {
    this.httpClient.updateConfig(config);
  }

  /**
   * Set base URL for API requests
   */
  setBaseURL(baseURL: string): void {
    this.httpClient.setBaseURL(baseURL);
  }

  /**
   * Set request timeout
   */
  setTimeout(timeout: number): void {
    this.httpClient.setTimeout(timeout);
  }

  /**
   * Add custom header to all requests
   */
  setHeader(name: string, value: string): void {
    this.httpClient.setHeader(name, value);
  }

  /**
   * Remove custom header from all requests
   */
  removeHeader(name: string): void {
    this.httpClient.removeHeader(name);
  }
}

// Create and export singleton instance
export const unifiedApiService = new UnifiedApiService();
export default unifiedApiService;